import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIdCard, faUpload, faTrash, faCheckCircle, faClock, faExclamationTriangle, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import ProviderShell, { useProviderApi } from './layouts/ProviderShell';

interface Profile {
    phone: string | null;
    bio: string;
    years_experience: number;
    license_number: string;
    license_expiry: string | null;
    is_license_expiring_soon: boolean;
}

interface Category {
    id: string;
    name: string;
    is_required: boolean;
    icon: string;
}

interface Qualification {
    id: number;
    category: { id: string; name: string; is_required: boolean };
    document_title: string;
    document_number: string;
    issuing_authority: string;
    issue_date: string | null;
    expiry_date: string | null;
    document_url: string | null;
    status: 'pending' | 'under_review' | 'verified' | 'rejected' | 'expired';
    rejection_reason: string;
    is_expired: boolean;
    submitted_at: string;
}

const STATUS_META: Record<string, { label: string; color: string; icon: any }> = {
    pending: { label: 'Pending Review', color: 'warning', icon: faClock },
    under_review: { label: 'Under Review', color: 'info', icon: faClock },
    verified: { label: 'Verified', color: 'success', icon: faCheckCircle },
    rejected: { label: 'Rejected', color: 'danger', icon: faExclamationTriangle },
    expired: { label: 'Expired', color: 'danger', icon: faExclamationTriangle },
};

const ProviderCredentials: React.FC = () => {
    const { api } = useProviderApi();

    const [profile, setProfile] = useState<Profile | null>(null);
    const [editingLicense, setEditingLicense] = useState(false);
    const [licenseForm, setLicenseForm] = useState({ phone: '', bio: '', years_experience: 0, license_number: '', license_expiry: '' });
    const [savingLicense, setSavingLicense] = useState(false);

    const [categories, setCategories] = useState<Category[]>([]);
    const [qualifications, setQualifications] = useState<Qualification[]>([]);
    const [loading, setLoading] = useState(true);

    const [showUpload, setShowUpload] = useState(false);
    const [uploadForm, setUploadForm] = useState({ category_id: '', document_title: '', document_number: '', issuing_authority: '', issue_date: '', expiry_date: '' });
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { document.title = 'Credentials - Uzima Healthcare'; }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [profileRes, catRes, qualRes] = await Promise.all([
                api.get('practitioners/me/'),
                api.get('practitioners/document-categories/'),
                api.get('practitioners/qualifications/'),
            ]);
            if (profileRes.data?.status === 'success') {
                const p = profileRes.data.data;
                setProfile(p);
                setLicenseForm({
                    phone: p.phone || '', bio: p.bio || '', years_experience: p.years_experience || 0,
                    license_number: p.license_number || '', license_expiry: p.license_expiry || '',
                });
            }
            if (catRes.data?.status === 'success') setCategories(catRes.data.data || []);
            if (qualRes.data?.status === 'success') setQualifications(qualRes.data.data || []);
        } catch { /* leave state as-is on failure */ } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const saveLicense = async () => {
        setSavingLicense(true);
        try {
            const res = await api.patch('practitioners/me/', licenseForm);
            if (res.data?.status === 'success') {
                setProfile(res.data.data);
                setEditingLicense(false);
                Swal.fire({ icon: 'success', title: 'Updated', timer: 1200, showConfirmButton: false });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update your license information.' });
        } finally {
            setSavingLicense(false);
        }
    };

    const submitUpload = async () => {
        if (!uploadForm.category_id || !uploadForm.document_title.trim() || !uploadFile) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Category, document title, and a file are all required.' });
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            Object.entries(uploadForm).forEach(([k, v]) => form.append(k, v));
            form.append('document_file', uploadFile);
            const res = await api.post('practitioners/qualifications/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Document Submitted', text: 'It will be reviewed shortly.', timer: 1800, showConfirmButton: false });
                setShowUpload(false);
                setUploadForm({ category_id: '', document_title: '', document_number: '', issuing_authority: '', issue_date: '', expiry_date: '' });
                setUploadFile(null);
                loadAll();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to submit document.' });
        } finally {
            setUploading(false);
        }
    };

    const removeQualification = async (q: Qualification) => {
        const confirm = await Swal.fire({ title: `Remove "${q.document_title}"?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545' });
        if (!confirm.isConfirmed) return;
        try {
            await api.delete(`practitioners/qualifications/${q.id}/`);
            loadAll();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to remove document.' });
        }
    };

    return (
        <ProviderShell>
            <div className="mb-4">
                <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faIdCard} className="me-2 text-primary" />Credentials</h2>
                <p className="text-muted mb-0">Your license details and supporting documents</p>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                </div>
            ) : (
                <>
                    <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <strong>Primary License</strong>
                            {!editingLicense ? (
                                <button className="btn btn-sm btn-primary" onClick={() => setEditingLicense(true)}>Edit</button>
                            ) : (
                                <div className="d-flex gap-2">
                                    <button className="btn btn-sm btn-light" onClick={() => setEditingLicense(false)} disabled={savingLicense}>Cancel</button>
                                    <button className="btn btn-sm btn-success" onClick={saveLicense} disabled={savingLicense}>{savingLicense ? 'Saving...' : 'Save'}</button>
                                </div>
                            )}
                        </div>
                        <div className="card-body">
                            {profile?.is_license_expiring_soon && (
                                <div className="alert alert-warning d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    Your license expires on {profile.license_expiry} — renew soon to avoid a lapse.
                                </div>
                            )}
                            {!editingLicense ? (
                                <div className="row g-3">
                                    <div className="col-md-4"><small className="text-muted d-block">License Number</small><strong>{profile?.license_number || 'Not provided'}</strong></div>
                                    <div className="col-md-4"><small className="text-muted d-block">Expiry Date</small><strong>{profile?.license_expiry || 'Not set'}</strong></div>
                                    <div className="col-md-4"><small className="text-muted d-block">Years of Experience</small><strong>{profile?.years_experience ?? 0}</strong></div>
                                    <div className="col-md-6"><small className="text-muted d-block">Phone</small><strong>{profile?.phone || 'Not provided'}</strong></div>
                                    <div className="col-12"><small className="text-muted d-block">Bio</small><span>{profile?.bio || 'Not provided'}</span></div>
                                </div>
                            ) : (
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label">License Number</label>
                                        <input type="text" className="form-control" value={licenseForm.license_number} onChange={(e) => setLicenseForm(f => ({ ...f, license_number: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Expiry Date</label>
                                        <input type="date" className="form-control" value={licenseForm.license_expiry} onChange={(e) => setLicenseForm(f => ({ ...f, license_expiry: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Years of Experience</label>
                                        <input type="number" min={0} className="form-control" value={licenseForm.years_experience} onChange={(e) => setLicenseForm(f => ({ ...f, years_experience: Number(e.target.value) }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Phone</label>
                                        <input type="text" className="form-control" value={licenseForm.phone} onChange={(e) => setLicenseForm(f => ({ ...f, phone: e.target.value }))} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Bio</label>
                                        <textarea className="form-control" rows={3} value={licenseForm.bio} onChange={(e) => setLicenseForm(f => ({ ...f, bio: e.target.value }))} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <strong>Supporting Documents</strong>
                            <button className="btn btn-sm btn-primary" onClick={() => setShowUpload(true)}>
                                <FontAwesomeIcon icon={faUpload} className="me-2" />Upload Document
                            </button>
                        </div>
                        <div className="card-body p-0">
                            {qualifications.length === 0 ? (
                                <div className="text-center py-5">
                                    <FontAwesomeIcon icon={faFileAlt} className="text-muted mb-3" style={{ fontSize: '2.5rem' }} />
                                    <h5>No documents uploaded yet</h5>
                                    <p className="text-muted">Upload certifications, insurance, or other credential documents for review.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light">
                                            <tr><th>Document</th><th>Category</th><th>Issuing Authority</th><th>Expiry</th><th>Status</th><th>Action</th></tr>
                                        </thead>
                                        <tbody>
                                            {qualifications.map(q => {
                                                const meta = STATUS_META[q.status] || STATUS_META.pending;
                                                return (
                                                    <tr key={q.id}>
                                                        <td>
                                                            {q.document_url ? (
                                                                <a href={q.document_url} target="_blank" rel="noreferrer">{q.document_title}</a>
                                                            ) : q.document_title}
                                                        </td>
                                                        <td>{q.category.name}</td>
                                                        <td>{q.issuing_authority || '—'}</td>
                                                        <td>{q.expiry_date || '—'}{q.is_expired && <span className="badge bg-danger ms-1">Expired</span>}</td>
                                                        <td><span className={`badge bg-${meta.color}-subtle text-${meta.color}`}><FontAwesomeIcon icon={meta.icon} className="me-1" />{meta.label}</span></td>
                                                        <td>
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => removeQualification(q)}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {showUpload && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Upload Credential Document</h5>
                                <button type="button" className="btn-close" onClick={() => setShowUpload(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Category</label>
                                    <select className="form-select" value={uploadForm.category_id} onChange={(e) => setUploadForm(f => ({ ...f, category_id: e.target.value }))}>
                                        <option value="">Select a category...</option>
                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}{c.is_required ? ' (Required)' : ''}</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Document Title</label>
                                    <input type="text" className="form-control" placeholder="e.g. Board Certification in Cardiology" value={uploadForm.document_title} onChange={(e) => setUploadForm(f => ({ ...f, document_title: e.target.value }))} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Document Number</label>
                                        <input type="text" className="form-control" value={uploadForm.document_number} onChange={(e) => setUploadForm(f => ({ ...f, document_number: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Issuing Authority</label>
                                        <input type="text" className="form-control" value={uploadForm.issuing_authority} onChange={(e) => setUploadForm(f => ({ ...f, issuing_authority: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Issue Date</label>
                                        <input type="date" className="form-control" value={uploadForm.issue_date} onChange={(e) => setUploadForm(f => ({ ...f, issue_date: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Expiry Date</label>
                                        <input type="date" className="form-control" value={uploadForm.expiry_date} onChange={(e) => setUploadForm(f => ({ ...f, expiry_date: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">File</label>
                                    <input type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowUpload(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={uploading} onClick={submitUpload}>{uploading ? 'Uploading...' : 'Submit for Review'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ProviderShell>
    );
};

export default ProviderCredentials;
